import type {
  ProspectStage,
  ProspectType,
  TaskStatus,
  TaskPriority,
  SprintStatus,
  ValidationResult,
  ValidationAudience,
  ActivityStatus,
} from "./types";

export type Tone = "neutral" | "info" | "success" | "warning" | "danger" | "accent";

export const TYPE_LABELS: Record<ProspectType, string> = {
  artista: "Artista",
  productor: "Productor",
  proveedor: "Proveedor",
};

export const STAGE_ORDER: ProspectStage[] = [
  "por_contactar",
  "contactado",
  "interesado",
  "pre_inscrito",
  "correo_enviado",
  "perfil_iniciado",
  "perfil_completo",
  "descartado",
];

export const STAGE_LABELS: Record<ProspectStage, string> = {
  por_contactar: "Por contactar",
  contactado: "Contactado",
  interesado: "Interesado",
  pre_inscrito: "Pre-inscrito",
  correo_enviado: "Correo enviado",
  perfil_iniciado: "Perfil iniciado",
  perfil_completo: "Perfil completo",
  descartado: "Descartado",
};

export const STAGE_TONE: Record<ProspectStage, Tone> = {
  por_contactar: "neutral",
  contactado: "neutral",
  interesado: "info",
  pre_inscrito: "info",
  correo_enviado: "accent",
  perfil_iniciado: "warning",
  perfil_completo: "success",
  descartado: "danger",
};

// Orden de columnas del board: Backlog leftmost.
export const TASK_COLUMNS: { value: TaskStatus; label: string }[] = [
  { value: "backlog", label: "Backlog" },
  { value: "pendiente", label: "Pendiente" },
  { value: "en_progreso", label: "En progreso" },
  { value: "en_revision", label: "En revisión" },
  { value: "listo", label: "Listo" },
];

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  backlog: "Backlog",
  pendiente: "Pendiente",
  en_progreso: "En progreso",
  en_revision: "En revisión",
  listo: "Listo",
};

export const SPRINT_STATUS_LABELS: Record<SprintStatus, string> = {
  activo: "Activo",
  cerrado: "Cerrado",
};

export const SPRINT_STATUS_TONE: Record<SprintStatus, Tone> = {
  activo: "success",
  cerrado: "neutral",
};

export const PRIORITY_LABELS: Record<TaskPriority, string> = {
  baja: "Baja",
  media: "Media",
  alta: "Alta",
};

export const PRIORITY_TONE: Record<TaskPriority, Tone> = {
  baja: "neutral",
  media: "info",
  alta: "danger",
};

export const RESULT_LABELS: Record<ValidationResult, string> = {
  no_probado: "No probado",
  en_prueba: "En prueba",
  funciona_ok: "Funciona OK",
  funciona_con_obs: "Funciona c/obs",
  falla: "Falla",
};

export const RESULT_TONE: Record<ValidationResult, Tone> = {
  no_probado: "neutral",
  en_prueba: "warning",
  funciona_ok: "success",
  funciona_con_obs: "info",
  falla: "danger",
};

export const RESULT_OPTIONS = (Object.keys(RESULT_LABELS) as ValidationResult[]).map((v) => ({
  value: v,
  label: RESULT_LABELS[v],
}));

export const AUDIENCE_LABELS: Record<ValidationAudience, string> = {
  productor: "Productores",
  artista: "Artistas",
  proveedor: "Proveedores",
};

export const ACTIVITY_LABELS: Record<ActivityStatus, string> = {
  sin_entrar: "Inscrito · sin entrar",
  activo: "Activo",
  explorando: "Explorando",
  reactivar: "Inactivo · reactivar",
};

export const ACTIVITY_TONE: Record<ActivityStatus, Tone> = {
  sin_entrar: "warning",
  activo: "success",
  explorando: "info",
  reactivar: "danger",
};

/** Áreas conocidas de tareas (del Excel). El campo es texto libre. */
export const TASK_AREAS = [
  "Branding",
  "Onboarding",
  "CRM",
  "Marketing",
  "Embajadores",
  "Ventas",
  "Cotizaciones",
  "Staff",
  "Compra",
];

/**
 * Próxima acción sugerida POR ETAPA. ESPEJO del catálogo backend
 * (`mn-server/.../Seguimiento/constants.ts` NEXT_ACTIONS_BY_STAGE). El 1er
 * elemento de cada etapa es el default. Mantener ambos sincronizados.
 */
export const NEXT_ACTIONS_BY_STAGE: Record<ProspectStage, string[]> = {
  por_contactar: ["Hacer primer contacto", "Buscar datos de contacto"],
  contactado: ["Hacer seguimiento / esperar respuesta", "Agendar llamada o reunión"],
  interesado: ["Enviar invitación de onboarding", "Resolver dudas / objeciones"],
  pre_inscrito: ["Enviar invitación de onboarding"],
  correo_enviado: ["Acompañar a que abra el link y complete su perfil", "Reenviar recordatorio"],
  perfil_iniciado: ["Empujar a que termine el perfil", "Reenviar recordatorio"],
  perfil_completo: ["Dar la bienvenida / activar uso", "Cerrado, sin acción"],
  descartado: ["Sin acción (descartado)", "Reactivar más adelante"],
};

/** Todas las acciones únicas (para detectar valores "custom" en el selector). */
export const ALL_NEXT_ACTIONS: string[] = Array.from(
  new Set(Object.values(NEXT_ACTIONS_BY_STAGE).flat()),
);

/** Orígenes de lead conocidos (espejo de LEAD_SOURCES backend). */
export const LEAD_SOURCES: string[] = [
  "Seguridad 360",
  "Instagram",
  "WhatsApp",
  "Referido",
  "Evento",
  "Contacto en frío",
  "Web / formulario",
  "Otro",
];

/** Etiquetas + tono de los tipos de evento del timeline de actividad. */
export const ACTIVITY_KIND_LABELS: Record<string, string> = {
  nota: "Nota",
  contacto: "Contacto",
  etapa: "Cambio de etapa",
  invitacion: "Invitación",
  recordatorio: "Recordatorio",
  sistema: "Sistema",
};

export const ACTIVITY_KIND_TONE: Record<string, Tone> = {
  nota: "neutral",
  contacto: "info",
  etapa: "accent",
  invitacion: "success",
  recordatorio: "warning",
  sistema: "neutral",
};
